import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { isEqual } from 'lodash';
import { Subscription } from 'rxjs';
import { SIGNAL_ITEM_KEY, TABLE_VIEW_MODES } from 'src/app/extensions/constants/commonConstants';
import { RESPONSIVE_BREAKPOINTS } from 'src/app/extensions/constants/responsive-breakpoints';
import { SpecialOfferAPIService } from 'src/app/extensions/services/api/specialOffer-api.service';
import { GlobalService } from 'src/app/extensions/services/global.service';
import { ScrollService } from 'src/app/extensions/services/scroll.service';
import { WindowEventsService } from 'src/app/extensions/services/window-events.services';
import { scrollTop, setTableViewMode } from 'src/app/extensions/utilities';
import { SpecialOfferFilterModalComponent } from '../special-offer-modals/special-offer-filter-modal/special-offer-filter-modal.component';
import { NotificationService } from 'src/app/core/services/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { SPECIAL_OFFER_ROUTES } from 'src/app/core/constants/routes-constants';

@Component({
  selector: 'app-special-offer-list',
  standalone: false,
  templateUrl: './special-offer-list.component.html',
  styleUrl: './special-offer-list.component.scss',
  providers: [ScrollService],
})
export class SpecialOfferListComponent implements OnInit, OnDestroy {
  hasData = false;
  public tableViewMode: string;
  public TABLE_VIEW_MODES = TABLE_VIEW_MODES;
  public windowSizeLgUp = true;
  public currentRoute: string;
  public subscriptions: Subscription[] = [];
  public showBackToTop = false;
  public activeFilterCount = 0;
  specialOffers: app.models.GetFilteredSpecialOfferModel[];
  private pageFilter: app.models.GetSpecialOffersFilterModel;
  public initialFilterState: app.models.GetSpecialOffersFilterModel;
  private pageIndex = 1;
  private maxIndex: number;
  public props: app.models.DynamicPropsDictionary;
  public hasFilterData = true;

  constructor(
    private _router: Router,
    private _windowEventService: WindowEventsService,
    private _globalService: GlobalService,
    private _specialOfferAPIService: SpecialOfferAPIService,
    private _scrollService: ScrollService,
    private _ngbModal: NgbModal,
    private _notificationService: NotificationService,
    private _translateService: TranslateService
  ) {
    this.currentRoute = this._router.url.split('/')[1];
  }

  ngOnInit(): void {
    this.tableViewMode = setTableViewMode(this._globalService, this.currentRoute);
    this.pageFilter = {
      fromDate: null,
      toDate: null,
      dictionaryCampaignTypeIds: [],
      statuses: [],
      dealerExternalEntityIds: [],
    };
    this.initialFilterState = structuredClone(this.pageFilter);
    this.loadData();
    let backToTopSubscription = this._scrollService.obsShowBackToTop.subscribe(x => {
      this.showBackToTop = x;
    });
    this.subscriptions.push(backToTopSubscription);

    this.windowSizeLgUp = window.innerWidth >= RESPONSIVE_BREAKPOINTS.lg;
    let resizeSubscription = this._windowEventService.onResize.subscribe(() => {
      this.windowSizeLgUp = window.innerWidth >= RESPONSIVE_BREAKPOINTS.lg;
    });
    this.subscriptions.push(resizeSubscription);

    let scrollSubscription = this._scrollService.obsFetchData.subscribe(() => {
      if (this.pageIndex <= this.maxIndex) {
        this.pageIndex++;
        this._specialOfferAPIService.getFilteredSpecialOffers(this.pageIndex, this.pageFilter).subscribe(res => {
          this.specialOffers = this.specialOffers.concat(res.data.values);

          if (this.pageIndex <= this.maxIndex) {
            this._scrollService.waitingForDataFetch = false;
          }
        });
      }
    });
    this.subscriptions.push(scrollSubscription);
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach(subscription => {
        subscription.unsubscribe();
      });
    }
  }

  public toggleViewMode(tableViewMode: string) {
    this.tableViewMode = tableViewMode;
    this._globalService.toggleTableViewMode(this.currentRoute, tableViewMode);
  }

  filterHandler() {
    let instance = this._ngbModal.open(SpecialOfferFilterModalComponent, {
      scrollable: true,
      centered: true,
    });
    instance.componentInstance.pageFilter = this.pageFilter;
    instance.componentInstance.initialFilterState = this.initialFilterState;

    instance.closed.subscribe(result => {
      if (result !== false) {
        this.activeFilterCount = result;
        this.pageIndex = 1;
        this.initialFilterState = {
          ...this.initialFilterState,
          fromDate: this.pageFilter.fromDate,
          toDate: this.pageFilter.toDate,
          dictionaryCampaignTypeIds: this.pageFilter.dictionaryCampaignTypeIds,
          statuses: this.pageFilter.statuses,
          dealerExternalEntityIds: this.pageFilter.dealerExternalEntityIds,
        };
        if (!isEqual(this.pageFilter, this.initialFilterState)) {
          this.pageFilter = structuredClone(this.initialFilterState);
        }
        this._specialOfferAPIService.getFilteredSpecialOffers(this.pageIndex, this.pageFilter).subscribe(res => {
          this.specialOffers = res.data.values;

          this.hasFilterData = this.specialOffers.length != 0;
          this.hasData = this.specialOffers.length != 0;
          this.props = res.props;
          this.maxIndex = res.data.totalPages;

          this._scrollService.waitingForDataFetch = this.pageIndex > this.maxIndex;
        });
      }
    });
  }

  navigateAddSpecialOfferHandler() {
    this._router.navigate([
      SPECIAL_OFFER_ROUTES.ADMIN_SPECIAL_OFFERS,
      SPECIAL_OFFER_ROUTES.NEW,
      SPECIAL_OFFER_ROUTES.CAMPAIGN_DETAILS,
    ]);
    this._globalService.setSignalItem(SIGNAL_ITEM_KEY.SPECIAL_OFFER_DATA, undefined);
  }

  loadData() {
    this._specialOfferAPIService.getFilteredSpecialOffers(this.pageIndex, this.pageFilter).subscribe(res => {
      this.specialOffers = res.data.values;
      this.hasData = this.specialOffers.length != 0;
      this.maxIndex = res.data.totalPages;
      this.props = res.props;

      this._scrollService.waitingForDataFetch = this.pageIndex > this.maxIndex;
    });
  }

  public resetFiltersHandler() {
    this.pageFilter.fromDate = null;
    this.pageFilter.toDate = null;
    this.pageFilter.dictionaryCampaignTypeIds = [];
    this.pageFilter.statuses = [];
    this.pageFilter.dealerExternalEntityIds = [];
    this.activeFilterCount = 0;

    this.initialFilterState = {
      ...this.initialFilterState,
      fromDate: this.pageFilter.fromDate,
      toDate: this.pageFilter.toDate,
      dictionaryCampaignTypeIds: this.pageFilter.dictionaryCampaignTypeIds,
      statuses: this.pageFilter.statuses,
      dealerExternalEntityIds: this.pageFilter.dealerExternalEntityIds,
    };

    if (!isEqual(this.pageFilter, this.initialFilterState)) {
      this.pageFilter = structuredClone(this.initialFilterState);
    }

    this.hasFilterData = true;
    this.pageIndex = 1;
    this.loadData();
  }

  deleteSpecialOffer(specialOfferId: string) {
    this._specialOfferAPIService.deleteSpecialOffer(specialOfferId).subscribe(res => {
      if (res.isSuccess) {
        this._notificationService.showSuccess(
          this._translateService.instant('specialOffers.wizard.notification.requestDeleted'),
          this._translateService.instant('common.success')
        );
        this.loadData();
      }
    });
  }

  refreshSpecialOfferList() {
    this.loadData();
  }

  scrollToTop(): void {
    scrollTop();
  }
}
